import { useEffect, useMemo, useRef, useState } from 'react'
import { Badge } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { roomName } from '../room/format'
import { BlockedMark } from '../status/BlockedMark'
import type { PaletteRoom } from '../room/useRooms'
import { SHORTCUTS } from './useKeyboard'

export type { PaletteRoom }

/**
 * Go to any room, across every organisation.
 *
 * ⌘K on a keyboard, and the room title on a phone — where there is no rail
 * listing the repositories and no key to press, this is the only way from one
 * room to another. That second role is why it knows which room you are in and
 * why it says so when a search matches nothing: a picker that is also the map
 * has to answer "where am I" as well as "where can I go".
 */
export function Palette({
  rooms, currentChannelId = null, onPick, onClose,
}: {
  rooms: PaletteRoom[]
  /** The room being shown behind the picker, marked rather than offered. */
  currentChannelId?: string | null
  onPick: (room: PaletteRoom) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  /**
   * Where the keyboard cursor is, and it starts on the room you are in.
   *
   * Zero would put the cursor's highlight on the first row while the "you are
   * here" dot sat three rows down — two marks competing to mean the same
   * thing, and on touch, where there is no cursor at all, only the wrong one
   * is legible. Starting here also makes ⌘K then ↓ move from where you are
   * rather than from the top of a list you did not choose the order of.
   */
  const [index, setIndex] = useState(() => {
    const at = rooms.findIndex((room) => room.channel.id === currentChannelId)
    return at < 0 ? 0 : at
  })
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Only where a keyboard is already out. On a phone, focusing the field
    // raises the on-screen keyboard over the list the tap just opened — and
    // with three repositories nobody wanted to search, they wanted to tap.
    // Typing still works: the field is one tap away and filters from there.
    if (window.matchMedia?.('(hover: hover)').matches ?? true) inputRef.current?.focus()
  }, [])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return rooms
    return rooms.filter(
      (room) =>
        room.channel.repository_key.toLowerCase().includes(needle) ||
        room.organisation.name.toLowerCase().includes(needle),
    )
  }, [rooms, query])

  return (
    <div
      role="dialog"
      aria-label="Go to a room"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.66)',
        backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center',
        // 90px of head room is a quarter of a phone screen above a box that is
        // about to have a keyboard under it. `clamp` keeps the desktop framing
        // and gives the height back where there is none.
        padding: 'clamp(20px, 12vh, 90px) max(20px, var(--safe-left)) 20px'
          + ' max(20px, var(--safe-right))',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(560px,100%)', background: 'var(--ground-raised)',
          border: '1px solid var(--border-strong)', borderRadius: radius.xl,
          overflow: 'hidden', boxShadow: 'var(--shadow-overlay)',
        }}
      >
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
          <input
            ref={inputRef}
            value={query}
            aria-label="Go to a room"
            placeholder="Go to a room…"
            onChange={(event) => { setQuery(event.target.value); setIndex(0) }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') { event.preventDefault(); setIndex(Math.min(matches.length - 1, index + 1)) }
              else if (event.key === 'ArrowUp') { event.preventDefault(); setIndex(Math.max(0, index - 1)) }
              else if (event.key === 'Enter') { event.preventDefault(); if (matches[index]) onPick(matches[index]) }
            }}
            className="sp-palette-field"
            style={{ width: '100%', font: '400 15px var(--font-machine)', color: 'var(--text)' }}
            // A phone's keyboard should offer none of these for a repository
            // name; the first capital letter of an autocorrected "Stagepost"
            // matches nothing.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <ul
          role="listbox"
          style={{
            // Taller than it was, because the keyboard no longer opens with
            // it on touch and the room after the third is worth reaching
            // without a scroll.
            maxHeight: 'min(420px, 55vh)', overflowY: 'auto', padding: 8, margin: 0,
            listStyle: 'none', overscrollBehavior: 'contain',
          }}
        >
          {matches.map((room, position) => {
            const here = room.channel.id === currentChannelId
            return (
            // `role="none"` on the item and `role="option"` on the control:
            // a listbox's children have to be options, and a bare `<button>`
            // announces itself as a button with an `aria-selected` that means
            // nothing on it. The option stays a real button so Tab still
            // reaches it — an option is allowed to be focusable.
            //
            // `aria-selected` and `aria-current` say different things here and
            // both are wanted: selected is where the cursor is, current is the
            // room you are actually in.
            <li key={room.channel.id} role="none">
              <button
                role="option"
                onClick={() => onPick(room)}
                aria-selected={position === index}
                aria-current={here}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  width: '100%', minHeight: 44, cursor: 'pointer', borderRadius: radius.sm,
                  background: position === index ? 'var(--panel-hover)' : 'transparent',
                }}
              >
                <span style={{ font: '400 10px var(--font-machine)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-faint)', minWidth: 62, textAlign: 'left' }}>
                  {room.organisation.name}
                </span>
                <span
                  title={room.channel.repository_key}
                  style={{ flex: 1, minWidth: 0, font: '500 13.5px var(--font-machine)', color: 'var(--text)', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {roomName(room.channel)}
                </span>
                {/* Where you already are. A dot rather than a word: this is
                    the only row in the list that is not somewhere to go, and
                    it should read as a position rather than as an option. */}
                {here && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6, height: 6, borderRadius: '50%', flex: 'none',
                      background: 'var(--text)',
                    }}
                  />
                )}
                <BlockedMark since={room.channel.blocked_since} />
                <Badge count={room.channel.unread} />
              </button>
            </li>
            )
          })}
          {!matches.length && (
            <li
              style={{
                padding: '18px 12px', fontFamily: 'var(--font-human)',
                fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)',
              }}
            >
              {rooms.length
                ? 'No room matches that.'
                : 'No rooms yet. One appears when an agent first speaks in a repository.'}
            </li>
          )}
        </ul>
        <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)' }}>
          {/* Keys on a machine that has them, and what to do on one that does
              not. Told apart by the pointer rather than by the width: a phone
              in landscape is 844px wide and still has no ⏎ to press. */}
          <span className="sp-pointer-only">↑↓ move · ⏎ open · esc close · ? for shortcuts</span>
          <span className="sp-touch-only">Tap a room, or type to filter</span>
        </div>
      </div>
    </div>
  )
}

export function Help({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="Keyboard"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.66)',
        backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(460px,100%)', background: 'var(--ground-raised)',
          border: '1px solid var(--border-strong)', borderRadius: radius.xl,
          padding: 22, boxShadow: 'var(--shadow-overlay)',
        }}
      >
        <h2 style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 16 }}>
          Keyboard
        </h2>
        {SHORTCUTS.map((shortcut) => (
          <div key={shortcut.keys} style={{ display: 'flex', alignItems: 'baseline', gap: 14, padding: '7px 0', borderTop: '1px solid var(--border)' }}>
            <kbd style={{ font: '500 11.5px var(--font-machine)', background: 'var(--panel-strong)', borderRadius: radius.xs, padding: '3px 8px', minWidth: 58, textAlign: 'center' }}>
              {shortcut.keys}
            </kbd>
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>{shortcut.what}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
